import { SUPPORTED_LANGS, translations } from './i18n.js';

const STORAGE_KEY = 'agent4live.lang';
const CONSENT_KEY = 'agent4live.consent';
const DEFAULT_LANG = 'en';
const parser = new DOMParser();

let trackEventRef = null;
const track = (name, params) => trackEventRef?.(name, params);

const detectInitialLang = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_LANGS.includes(saved)) return saved;

  const browser = navigator.language?.toLowerCase().slice(0, 2);
  if (browser && SUPPORTED_LANGS.includes(browser)) return browser;

  return DEFAULT_LANG;
};

// Translations may contain inline tags like <strong> or <code>.
// DOMParser parses HTML without executing <script> or inline handlers — XSS-safe by design.
// We still trust the source: translations come from a static module, not user input.
const setI18nContent = (el, value) => {
  if (!value.includes('<')) {
    el.textContent = value;
    return;
  }
  const fragment = parser.parseFromString(value, 'text/html').body;
  el.replaceChildren(...fragment.childNodes);
};

const applyLang = (lang) => {
  const dict = translations[lang];
  if (!dict) return;

  document.documentElement.lang = lang;
  document.documentElement.dataset.lang = lang;

  for (const el of document.querySelectorAll('[data-i18n]')) {
    const value = dict[el.dataset.i18n];
    if (value !== undefined) setI18nContent(el, value);
  }

  for (const el of document.querySelectorAll('[data-i18n-aria]')) {
    const value = dict[el.dataset.i18nAria];
    if (value !== undefined) el.setAttribute('aria-label', value);
  }

  for (const el of document.querySelectorAll('[data-i18n-alt]')) {
    const value = dict[el.dataset.i18nAlt];
    if (value !== undefined) el.setAttribute('alt', value);
  }

  for (const el of document.querySelectorAll('[data-lang-display]')) {
    el.textContent = lang.toUpperCase();
  }

  for (const el of document.querySelectorAll('[role="menuitemradio"][data-lang]')) {
    el.setAttribute('aria-checked', el.dataset.lang === lang ? 'true' : 'false');
  }
};

const setLang = (lang) => {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  localStorage.setItem(STORAGE_KEY, lang);
  applyLang(lang);
  track('language_change', { language: lang });
};

applyLang(detectInitialLang());

const langSwitcher = document.querySelector('[data-testid="lang-switcher"]');
const langButton = document.querySelector('[data-testid="lang-toggle"]');
const langMenu = document.querySelector('[data-testid="lang-menu"]');

const setLangMenuOpen = (open) => {
  if (!langMenu || !langButton) return;
  langMenu.hidden = !open;
  langButton.setAttribute('aria-expanded', String(open));
  if (open) {
    const active =
      langMenu.querySelector('[aria-checked="true"]') ?? langMenu.querySelector('button');
    active?.focus();
  }
};

langButton?.addEventListener('click', () => {
  setLangMenuOpen(langMenu?.hidden === true);
});

langMenu?.addEventListener('click', (event) => {
  const target = event.target.closest('[data-lang]');
  if (!target) return;
  setLang(target.dataset.lang);
  setLangMenuOpen(false);
  langButton?.focus();
});

langMenu?.addEventListener('keydown', (event) => {
  const items = [...langMenu.querySelectorAll('button[data-lang]')];
  const index = items.indexOf(document.activeElement);
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    items[(index + 1) % items.length]?.focus();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    items[(index - 1 + items.length) % items.length]?.focus();
  } else if (event.key === 'Home') {
    event.preventDefault();
    items[0]?.focus();
  } else if (event.key === 'End') {
    event.preventDefault();
    items[items.length - 1]?.focus();
  }
});

document.addEventListener('click', (event) => {
  if (langMenu?.hidden) return;
  if (!langSwitcher?.contains(event.target)) setLangMenuOpen(false);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && langMenu && !langMenu.hidden) {
    setLangMenuOpen(false);
    langButton?.focus();
  }
});

// ---------- Consent + lazy analytics ----------

const banner = document.querySelector('[data-testid="consent-banner"]');
const showBanner = () => {
  if (banner) banner.hidden = false;
};
const hideBanner = () => {
  if (banner) banner.hidden = true;
};

const enableAnalytics = async () => {
  try {
    const { initAnalytics, trackEvent } = await import('./analytics.js');
    initAnalytics();
    trackEventRef = trackEvent;
  } catch (err) {
    console.error('Failed to initialize analytics', err);
  }
};

const consent = localStorage.getItem(CONSENT_KEY);
if (consent === 'accepted') {
  enableAnalytics();
} else if (consent === null) {
  showBanner();
}

document.querySelector('[data-testid="consent-accept"]')?.addEventListener('click', () => {
  localStorage.setItem(CONSENT_KEY, 'accepted');
  hideBanner();
  enableAnalytics();
});

document.querySelector('[data-testid="consent-reject"]')?.addEventListener('click', () => {
  localStorage.setItem(CONSENT_KEY, 'rejected');
  hideBanner();
});

document.querySelector('[data-testid="consent-revoke"]')?.addEventListener('click', (event) => {
  event.preventDefault();
  localStorage.removeItem(CONSENT_KEY);
  showBanner();
});

// ---------- Custom event tracking ----------

document.querySelector('[data-testid="hero-cta-primary"]')?.addEventListener('click', () => {
  track('file_download', {
    file_name: 'agent4live.amxd',
    file_extension: 'amxd',
    link_url: '/agent4live.amxd',
    link_text: 'Download agent4live',
  });
});

document.querySelector('[data-testid="header-github"]')?.addEventListener('click', () => {
  track('select_content', { content_type: 'github', item_id: 'github_header' });
});

document.querySelector('[data-testid="header-x"]')?.addEventListener('click', () => {
  track('select_content', { content_type: 'social', item_id: 'x_header' });
});

// ---------- GitHub release version chip ----------
// __APP_VERSION__ and __GITHUB_REPO__ are inlined by Vite (see vite.config.js).
// Display priority: build-time baseline → localStorage cache → runtime fetch.
// localStorage cache survives GitHub API rate-limits (60/h/IP unauth) across reloads.

const VERSION_CACHE_KEY = 'agent4live.version';
const versionEl = document.querySelector('[data-version]');

const setVersion = (v) => {
  if (!versionEl || !v) return;
  versionEl.textContent = v;
  versionEl.hidden = false;
};

setVersion(__APP_VERSION__ ?? localStorage.getItem(VERSION_CACHE_KEY));

fetch(`https://api.github.com/repos/${__GITHUB_REPO__}/releases/latest`, {
  headers: { Accept: 'application/vnd.github+json' },
})
  .then((res) => (res.ok ? res.json() : null))
  .then((data) => {
    if (!data?.tag_name) return;
    setVersion(data.tag_name);
    localStorage.setItem(VERSION_CACHE_KEY, data.tag_name);
  })
  .catch(() => {});
